import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Send, Star, PartyPopper, Share2, ExternalLink } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { StarRating } from './StarRating';
import { useLang } from '../i18n';
import { useReviews } from '../reviews';

const fmtDate = (iso, lang) => {
  try {
    return new Date(iso).toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-US', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch (e) { return ''; }
};

const CONFETTI_COLORS = ['#0f766e', '#2dd4bf', '#f59e0b', '#f472b6', '#38bdf8', '#a3e635'];

const TRUSTPILOT_GREEN = '#00b67a';
const TRUSTPILOT_EVALUATE_FALLBACK = 'https://www.trustpilot.com/evaluate/barcodestudio.net';

const Confetti = () => {
  const pieces = React.useMemo(() => Array.from({ length: 16 }).map((_, i) => {
    const angle = (Math.PI * 2 * i) / 16 + (Math.random() * 0.5 - 0.25);
    const dist = 55 + Math.random() * 55;
    return {
      id: `confetti-${i}`,
      dx: Math.cos(angle) * dist,
      dy: Math.sin(angle) * dist - 18,
      rot: Math.random() * 720 - 360,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      delay: Math.random() * 0.08,
    };
  }), []);
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" data-testid="review-confetti" aria-hidden="true">
      {pieces.map((p) => (
        <span
          key={p.id}
          className="bc-confetti-piece"
          style={{ background: p.color, '--dx': `${p.dx}px`, '--dy': `${p.dy}px`, '--rot': `${p.rot}deg`, animationDelay: `${p.delay}s` }}
        />
      ))}
    </div>
  );
};

export const ReviewSection = () => {
  const { t, lang } = useLang();
  const { summary, reviews, loading, submit, shareApp, trustpilot } = useReviews();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState(''); // honeypot
  const [sending, setSending] = useState(false);
  const [justRated, setJustRated] = useState(false);
  const [sharePrompt, setSharePrompt] = useState(false);

  // Prefill from the user's existing review (if any).
  useEffect(() => {
    if (summary.my_review) {
      setRating(summary.my_review.rating || 0);
      setComment(summary.my_review.comment || '');
      setName(summary.my_review.name || '');
    }
  }, [summary.my_review]);

  const countWord = summary.count === 1 ? t('reviews_one') : t('reviews_many');
  const hasReviews = summary.count > 0;
  const alreadyRated = !!summary.my_review;
  const de = lang === 'de';
  const tpCount = Number(summary.trustpilot_count || 0);
  const evaluateUrl = (trustpilot && trustpilot.evaluate_url) || TRUSTPILOT_EVALUATE_FALLBACK;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (company.trim()) return; // bot
    if (rating < 1 || rating > 5) {
      toast.error(t('review_choose_stars'));
      return;
    }
    setSending(true);
    try {
      await submit({ rating, comment: comment.trim(), name: name.trim(), company });
      toast.success(t('review_thanks'));
      setJustRated(true);
      setSharePrompt(true);
      setTimeout(() => setJustRated(false), 2800);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || t('review_error'));
    } finally {
      setSending(false);
    }
  };

  const handleShare = () => shareApp(t, 'review_thanks');

  return (
    <section id="reviews" className="mt-10 scroll-mt-24" data-testid="review-section" aria-labelledby="reviews-heading">
      <div className="mb-5 text-center">
        <h2 id="reviews-heading" className="font-heading text-xl font-semibold text-foreground sm:text-2xl">
          {t('reviews_title')}
        </h2>
        <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{t('reviews_subtitle')}</p>
      </div>

      <div className="grid gap-4 md:grid-cols-5">
        {/* Summary */}
        <Card className="md:col-span-2" data-testid="review-summary">
          <CardContent className="relative flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
            {justRated && <Confetti />}
            {hasReviews ? (
              <>
                <div
                  className={`font-heading text-5xl font-bold text-foreground ${justRated ? 'bc-pop' : ''}`}
                  data-testid="review-average"
                >
                  {Number(summary.average).toFixed(1)}
                </div>
                <StarRating value={summary.average} size={22} />
                <p className="text-sm text-muted-foreground" data-testid="review-count">
                  {summary.count} {countWord} · {t('reviews_of_5')}
                </p>
                {tpCount > 0 && (
                  <p className="text-xs text-muted-foreground" data-testid="review-tp-note">
                    {de
                      ? `inkl. ${tpCount} ${tpCount === 1 ? 'Trustpilot-Bewertung' : 'Trustpilot-Bewertungen'}`
                      : `incl. ${tpCount} Trustpilot ${tpCount === 1 ? 'review' : 'reviews'}`}
                  </p>
                )}
                <div className="mt-2 w-full max-w-[220px] space-y-1">
                  {[5, 4, 3, 2, 1].map((s) => {
                    const c = (summary.distribution && summary.distribution[String(s)]) || 0;
                    const pct = summary.count ? Math.round((c / summary.count) * 100) : 0;
                    return (
                      <div key={s} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="flex w-6 items-center justify-end gap-0.5">{s}<Star className="h-3 w-3 fill-amber-400 text-amber-400" /></span>
                        <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-secondary">
                          <span className="block h-full rounded-full bg-amber-400 transition-[width] duration-300" style={{ width: `${pct}%` }} />
                        </span>
                        <span className="w-6 text-right">{c}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <>
                <StarRating value={0} size={26} />
                <p className="text-sm text-muted-foreground">{t('reviews_none')}</p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Submit form */}
        <Card className="md:col-span-3" data-testid="review-form-card">
          <CardContent className="p-6">
            {(sharePrompt || (alreadyRated && summary.my_review)) && (
              <div
                className={`mb-4 flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 p-3 ${justRated ? 'bc-thanks-in' : ''}`}
                data-testid="review-thanks-banner"
                role="status"
                aria-live="polite"
              >
                <PartyPopper className="bc-pop h-6 w-6 shrink-0 text-primary" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground">{t('review_thanks_title')}</p>
                  <p className="text-xs text-muted-foreground">{t('review_thanks_sub')}</p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleShare}
                  className="shrink-0 gap-1.5 border-primary/40 text-primary hover:bg-primary/10 hover:text-primary"
                  data-testid="review-share-btn"
                >
                  <Share2 className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('review_share')}</span>
                </Button>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden" style={{ opacity: 0 }}>
                <label htmlFor="review-company">Company</label>
                <input id="review-company" tabIndex={-1} autoComplete="off" value={company} onChange={(e) => setCompany(e.target.value)} />
              </div>

              <div className="space-y-1.5">
                <span className="text-sm font-medium text-foreground">
                  {alreadyRated ? t('review_you_rated') : t('review_your_rating')}
                </span>
                <div><StarRating value={rating} onChange={setRating} size={30} ariaLabel={t('review_your_rating')} /></div>
              </div>

              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t('review_comment_ph')}
                rows={3}
                maxLength={1000}
                aria-label={t('review_comment_ph')}
                data-testid="review-comment-input"
              />

              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('review_name_ph')}
                maxLength={80}
                aria-label={t('review_name_ph')}
                data-testid="review-name-input"
              />

              <Button type="submit" disabled={sending} className="gap-2" data-testid="review-submit-btn">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sending ? t('review_sending') : (alreadyRated ? t('review_update') : t('review_submit'))}
              </Button>

              {alreadyRated && summary.my_review && summary.my_review.comment && !summary.my_review.comment_approved && (
                <p className="text-xs text-amber-600 dark:text-amber-500" data-testid="review-pending-note">
                  {t('review_pending_note')}
                </p>
              )}
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Trustpilot CTA — combined rating also reflects live Trustpilot reviews */}
      <div className="mt-4 flex justify-center" data-testid="review-trustpilot-cta-wrap">
        <a
          href={evaluateUrl}
          target="_blank"
          rel="noopener noreferrer nofollow"
          data-testid="review-trustpilot-cta"
          className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-transform duration-150 hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          style={{ backgroundColor: TRUSTPILOT_GREEN }}
        >
          <Star className="h-4 w-4 fill-current" aria-hidden="true" />
          {de ? 'Auf Trustpilot bewerten' : 'Review us on Trustpilot'}
          <ExternalLink className="h-4 w-4" />
        </a>
      </div>

      {/* Recent reviews with comments */}
      {reviews.length > 0 && (
        <div className="mt-5" data-testid="review-list">
          <h3 className="mb-3 font-heading text-sm font-semibold text-foreground">{t('reviews_recent')}</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {reviews.map((r) => (
              <Card key={r.id} data-testid={`review-item-${r.id}`}>
                <CardContent className="space-y-2 p-4">
                  <div className="flex items-center justify-between gap-2">
                    <StarRating value={r.rating} size={16} />
                    <span className="text-xs text-muted-foreground">{fmtDate(r.created_at, lang)}</span>
                  </div>
                  {r.comment && <p className="text-sm leading-relaxed text-foreground">{r.comment}</p>}
                  {r.name && <p className="text-xs font-medium text-muted-foreground">— {r.name}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
      {loading && !hasReviews && (
        <p className="mt-4 text-center text-xs text-muted-foreground">…</p>
      )}
    </section>
  );
};
